// src/screens/LeagueDetailScreen.js
// League home: roster + registration (join/leave), live standings, and the
// round-by-round schedule. League matches are ordinary H2H events (stamped
// leagueId/leagueRound) so each row links to the normal EventDetail screen.
// Deep-linkable at /league/:leagueId (public read, like events).
import React, { useEffect, useMemo, useState } from 'react';
import {
  View, Text, SafeAreaView, ScrollView, StyleSheet,
  TouchableOpacity, ActivityIndicator, TextInput,
} from 'react-native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  doc, onSnapshot, collection, query, where, getDocs, limit,
} from 'firebase/firestore';
import { auth, db } from '../../firebaseConfig';

import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { useError } from '../components/ErrorContext';
import BuyCritsModal from '../components/BuyCritsModal';
import { GAME_PROFILES } from '../constants/gameData';
import { gameUsesManualResults, resolveGameProfile } from '../constants/communityGames';
import useCritsEnabled from '../hooks/useCritsEnabled';
import { fmtCrits } from '../utils/currencyHelper';
import { describeCalendar } from '../utils/leagueSchedule';

const GOLD = '#FCD000';

const tsMs = (v) => (v?.toMillis ? v.toMillis() : (v?.seconds != null ? v.seconds * 1000 : NaN));
const fmtWhen = (ms) => {
  if (!Number.isFinite(ms)) return '—';
  const d = new Date(ms);
  return `${d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })} ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
};

const STATUS_LABELS = {
  registration: 'Registration Open',
  active: 'In Progress',
  finalizing: 'Finalizing',
  complete: 'Complete',
  cancelled: 'Cancelled',
};

const MATCH_STATUS_LABEL = (s) => {
  if (['completed', 'finished'].includes(s)) return 'Final';
  if (s === 'live') return 'LIVE';
  if (s === 'cancelled') return 'Cancelled';
  return 'Scheduled';
};

export default function LeagueDetailScreen({ navigation, route }) {
  const leagueId = route?.params?.leagueId || route?.params?.id;
  const { showError } = useError();
  const [league, setLeague] = useState(undefined); // undefined=loading, null=missing
  const [matches, setMatches] = useState([]);
  const [busy, setBusy] = useState(false);
  const [runningItBack, setRunningItBack] = useState(false);
  const [critShortfall, setCritShortfall] = useState(null);
  const uid = auth.currentUser?.uid || null;
  // Evergreen step 2.3: when Crits are off the entry / pool / payout readouts
  // hide and the join + share buttons drop their amounts (the server charges
  // 0). The callables and the slots-full -> purchaseLeagueSlot retry stay.
  const critsOn = useCritsEnabled();

  useEffect(() => {
    if (!leagueId) return;
    const unsub = onSnapshot(doc(db, 'leagues', leagueId), (snap) => {
      setLeague(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    }, () => setLeague(null));
    return () => unsub();
  }, [leagueId]);

  // All this league's match events, grouped by round below. Single-equality
  // query; sorted client-side to avoid needing the composite before it builds.
  useEffect(() => {
    if (!leagueId) return;
    const q = query(collection(db, 'tournaments'), where('leagueId', '==', leagueId));
    const unsub = onSnapshot(q, (snap) => {
      setMatches(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [leagueId]);

  const byRound = useMemo(() => {
    const map = new Map();
    for (const m of matches) {
      const r = Number(m.leagueRound || 0);
      if (!map.has(r)) map.set(r, []);
      map.get(r).push(m);
    }
    for (const arr of map.values()) arr.sort((x, y) => String(x.id).localeCompare(String(y.id)));
    return [...map.entries()].sort((x, y) => x[0] - y[0]);
  }, [matches]);

  const isRaceLeague = league?.roundFormat === 'race';
  // Warzone BR leagues run race-style rounds, but their lobby code is a
  // private-match GAME CODE, not a GT7 Room ID (copy-only difference here).
  const isBRLeague = !!GAME_PROFILES?.[league?.game]?.formats?.[league?.formatName]?.battleRoyale;

  // The full calendar — every round's planned start, so joiners see the
  // whole season before committing (founder 2026-08-18). Seeded
  // rounds.{N}.startAtMs wins (a late round can push the next one), then the
  // explicit calendar (roundTimesMs), then the day-anchored cadence grid;
  // sub-day cadences (reactive) only know round 1. Rounds beyond totalRounds
  // never render (an h2h league that locked short leaves calendar tail unused).
  const plannedRounds = useMemo(() => {
    if (!league) return [];
    const total = Math.max(0, Math.floor(Number(league.totalRounds || 0)));
    const times = Array.isArray(league.roundTimesMs) ? league.roundTimesMs : null;
    const startAtMs = tsMs(league.startAt);
    const gapMin = Number(league.interRoundGapMinutes || 0);
    const created = new Map(byRound);
    const out = [];
    for (let r = 1; r <= total; r++) {
      const seeded = Number(league.rounds?.[r]?.startAtMs);
      const evStart = tsMs(created.get(r)?.[0]?.startAt);
      const planned = Number.isFinite(seeded) && seeded > 0 ? seeded
        : Number.isFinite(evStart) ? evStart
          : (times && Number.isFinite(times[r - 1])) ? times[r - 1]
            : (r === 1 && Number.isFinite(startAtMs)) ? startAtMs
              : (gapMin >= 1440 && Number.isFinite(startAtMs)) ? startAtMs + (r - 1) * gapMin * 60000
                : null;
      out.push({ round: r, startMs: planned, matches: created.get(r) || [] });
    }
    // Defensive: events for rounds past totalRounds (shouldn't happen) still show.
    for (const [r, ms] of byRound) if (r > total) out.push({ round: r, startMs: tsMs(ms[0]?.startAt), matches: ms });
    return out;
  }, [league, byRound]);
  const calendarRhythm = describeCalendar(Array.isArray(league?.roundTimesMs) ? league.roundTimesMs : []);

  // Standings rows: finalStandings when complete, else live sort — race
  // leagues by points/wins, h2h by W (full tiebreaks incl. head-to-head are
  // applied server-side at completion).
  const standingsRows = useMemo(() => {
    if (!league) return [];
    if (league.finalStandings?.length) return league.finalStandings;
    const s = league.standings || {};
    const rows = (league.rosterUids || []).map((u) => ({
      userId: u,
      userName: league.roster?.[u]?.userName || 'Player',
      w: s[u]?.w || 0, l: s[u]?.l || 0,
      points: s[u]?.points || 0, wins: s[u]?.wins || 0,
      forfeits: s[u]?.forfeits || 0, played: s[u]?.played || 0,
    }));
    rows.sort(isRaceLeague
      ? (x, y) => (y.points - x.points) || (y.wins - x.wins) || (x.forfeits - y.forfeits) || x.userName.localeCompare(y.userName)
      : (x, y) => (y.w - x.w) || (x.forfeits - y.forfeits) || x.userName.localeCompare(y.userName));
    return rows.map((row, i) => ({ rank: i + 1, ...row }));
  }, [league, isRaceLeague]);

  // Lineup management (team leagues, captains): pick exactly teamSize
  // starters from the squad; alternates sit out. Applies to every match of
  // theirs that hasn't locked yet.
  const myTeamInfo = uid ? league?.roster?.[uid] : null;
  const isCaptainHere = !!myTeamInfo?.teamId;

  // Funding: the captain's join only RESERVES the spot — every squad member
  // pays their own share; unfunded teams are released + refunded at lock.
  const mySquadEntry = useMemo(() => {
    if (!uid || !league?.roster) return null;
    for (const [cap, info] of Object.entries(league.roster)) {
      if (info?.teamId && (info.squadUids || []).includes(uid)) return { captainUid: cap, info };
    }
    return null;
  }, [uid, league]);
  const [payingShare, setPayingShare] = useState(false);
  const payShare = async () => {
    setPayingShare(true);
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'payLeagueShare');
      await fn({ leagueId, captainUid: mySquadEntry.captainUid });
    } catch (err) {
      const msg = String(err?.message || '');
      if (msg.includes('insufficient-crits')) {
        const have = Number((msg.match(/have\s+(\d+)/i) || [])[1]);
        const need = Number((msg.match(/need\s+(\d+)/i) || [])[1]);
        setCritShortfall({ have: Number.isFinite(have) ? have : 0, need: Number.isFinite(need) ? need : (league?.entryCostCrits || 0) });
      } else {
        showError(critsOn ? 'Share not paid' : 'Share not confirmed', msg.replace(/^[a-z-]+:\s*/i, '') || 'Please try again.');
      }
    } finally {
      setPayingShare(false);
    }
  };
  const [lineupDraft, setLineupDraft] = useState(null); // null = mirror saved
  const [savingLineup, setSavingLineup] = useState(false);
  const savedLineup = myTeamInfo?.lineupUids || myTeamInfo?.teamRosterUids || [];
  const lineup = lineupDraft || savedLineup;
  const lineupDirty = !!lineupDraft
    && (lineupDraft.length !== savedLineup.length || lineupDraft.some((m) => !savedLineup.includes(m)));
  const toggleStarter = (memberUid) => {
    const cur = new Set(lineup);
    if (cur.has(memberUid)) cur.delete(memberUid);
    else if (cur.size < (league?.teamSize || 0)) cur.add(memberUid);
    setLineupDraft([...cur]);
  };
  const saveLineup = async () => {
    setSavingLineup(true);
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'setLeagueLineup');
      await fn({ leagueId, lineupUids: lineup });
      setLineupDraft(null);
    } catch (err) {
      showError('Lineup not saved', String(err?.message || '').replace(/^[a-z-]+:\s*/i, '') || 'Please try again.');
    } finally {
      setSavingLineup(false);
    }
  };

  // Room-ID submission (race leagues, this round's lobby host)
  const [roomIdInput, setRoomIdInput] = useState('');
  const [submittingRoom, setSubmittingRoom] = useState(false);
  const submitRoomId = async (eventDocId) => {
    const code = roomIdInput.trim().toLowerCase();
    if (!/^[a-z0-9]{6}$/.test(code)) {
      if (isBRLeague) {
        showError('Invalid game code', 'Game codes are 6 characters - letters and digits.');
      } else {
        showError('Invalid Room ID', 'GT7 Room IDs are 6 characters — lowercase letters and digits.');
      }
      return;
    }
    setSubmittingRoom(true);
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'submitMatchRoomId');
      await fn({ eventId: eventDocId, matchId: eventDocId, roomId: code });
      setRoomIdInput('');
    } catch (err) {
      showError('Room ID failed', String(err?.message || 'Please try again.'));
    } finally {
      setSubmittingRoom(false);
    }
  };

  const isMember = !!uid && (league?.rosterUids || []).includes(uid);
  const isCreator = !!uid && league?.createdByUid === uid;
  const spotsLeft = league ? Math.max(0, (league.maxPlayers || 0) - (league.rosterUids || []).length) : 0;
  const canJoin = league?.status === 'registration' && !isMember && spotsLeft > 0;
  const canLeave = league?.status === 'registration' && isMember && !isCreator;
  const canCancel = league?.status === 'registration' && isCreator;

  const call = async (name, confirmMsg) => {
    if (!auth.currentUser) { navigation.navigate('Login'); return; }
    if (confirmMsg && typeof window !== 'undefined' && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      const args = { leagueId };
      if (name === 'joinLeague' && (league?.isTeamLeague || Number(league?.teamSize || 1) > 1)) {
        // Team league: captains enter with their squad — find the team this
        // user OWNS (captains only; the server re-checks).
        const teamSnap = await getDocs(query(
          collection(db, 'teams'), where('ownerId', '==', uid), limit(1)));
        if (teamSnap.empty) {
          showError('Captains only',
            `This is a ${league.teamSize}v${league.teamSize} team league — only team captains can enter. Create a team (with ${league.teamSize}+ members) from the Your Team page first.`);
          setBusy(false);
          return;
        }
        args.teamId = teamSnap.docs[0].id;
      }
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), name);
      await fn(args);
    } catch (err) {
      const msg = String(err?.message || '');
      if (msg.includes('insufficient-crits')) {
        const have = Number((msg.match(/have\s+(\d+)/i) || [])[1]);
        const need = Number((msg.match(/need\s+(\d+)/i) || [])[1]);
        setCritShortfall({
          have: Number.isFinite(have) ? have : 0,
          need: Number.isFinite(need) ? need : (league?.entryCostCrits || 0),
        });
      } else if (msg.includes('league-slots-full')) {
        // Out of concurrent-league slots — offer the permanent Crit unlock
        // (the server recomputes the authoritative price), then retry.
        const detail = msg.split('league-slots-full:')[1]?.trim() || 'All your league slots are in use.';
        if (typeof window !== 'undefined' && window.confirm(`${detail}\n\nUnlock it now?`)) {
          try {
            await httpsCallable(getFunctions(undefined, 'us-central1'), 'purchaseLeagueSlot')({});
            setBusy(false);
            return call(name); // slot secured — retry the join
          } catch (e2) {
            const m2 = String(e2?.message || '');
            if (m2.includes('insufficient-crits')) {
              const have2 = Number((m2.match(/have\s+(\d+)/i) || [])[1]);
              const need2 = Number((m2.match(/need\s+(\d+)/i) || [])[1]);
              setCritShortfall({
                have: Number.isFinite(have2) ? have2 : 0,
                need: Number.isFinite(need2) ? need2 : 0,
              });
            } else {
              showError('Could not unlock slot', m2.replace(/^[a-z-]+:\s*/i, '') || 'Please try again.');
            }
          }
        }
      } else if (msg.includes('league-schedule-conflict:')) {
        showError('Schedule conflict',
          msg.split('league-schedule-conflict:')[1]?.trim() || 'This league\'s rounds overlap another league you\'re in.');
      } else {
        showError('League action failed', msg.replace(/^[a-z-]+:\s*/i, '') || 'Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  // Commissioner-only Run It Back — clones this complete league onto next
  // week's same slot and bell-recalls everyone who showed up. Backend gates
  // on the certified fill evaluation; this is the UI affordance.
  const runItBack = async () => {
    if (!auth.currentUser) { navigation.navigate('Login'); return; }
    if (typeof window !== 'undefined'
      && !window.confirm('Run it back? Reruns the same league for the same crowd — next week, same time. Everyone who showed up gets the call.')) return;
    setRunningItBack(true);
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'runItBackLeague');
      const res = await fn({ leagueId });
      const newId = res?.data?.leagueId;
      if (newId) navigation.navigate('LeagueDetail', { leagueId: newId });
    } catch (err) {
      const msg = String(err?.message || '');
      const prefix = ['run-it-back-locked', 'run-it-back-pending', 'run-it-back-team', 'run-it-back-lead']
        .find((p) => msg.includes(`${p}:`));
      showError('Unable to run it back',
        prefix ? (msg.split(`${prefix}:`)[1]?.trim() || 'Please try again.')
          : (msg.replace(/^[a-z-]+:\s*/i, '') || 'Please try again.'));
    } finally {
      setRunningItBack(false);
    }
  };

  if (league === undefined) {
    return (
      <SafeAreaView style={st.safe}>
        <Header navigation={navigation} />
        <ActivityIndicator color={GOLD} style={{ marginTop: 60 }} />
        <NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }
  if (league === null) {
    return (
      <SafeAreaView style={st.safe}>
        <Header navigation={navigation} />
        <Text style={[st.subtle, { margin: 24 }]}>This league doesn't exist (or was removed).</Text>
        <NavBar navigation={navigation} />
      </SafeAreaView>
    );
  }

  // Community/catalog games resolve through the open-catalog profile (the
  // league doc carries gameName for cg_* games).
  const dn = GAME_PROFILES?.[league.game]?.displayName
    || resolveGameProfile(league.game, league.gameName)?.displayName
    || league.gameName || league.game;
  const startMs = tsMs(league.startAt);
  const splits = (league.payoutMode === 'winner' && [100])
    || (league.payoutMode === 'podium' && [60, 30, 10])
    || null; // tiered shown from payoutSummary/finalStandings when known

  return (
    <SafeAreaView style={st.safe}>
      <Header navigation={navigation} />
      <ScrollView contentContainerStyle={st.container}>
        {/* Hero */}
        <View style={st.hero}>
          <View style={st.heroTop}>
            <Text style={st.title} numberOfLines={2}>{league.name}</Text>
            <Text style={[st.statusChip, league.status === 'active' && st.statusActive,
              league.status === 'complete' && st.statusDone,
              league.status === 'cancelled' && st.statusCancelled]}>
              {STATUS_LABELS[league.status] || league.status}
            </Text>
          </View>
          <Text style={st.subtle}>
            {dn}{league.platform ? ` · ${league.platform}` : ''} · {league.formatName} · League
            {'  ·  '}Commissioner: {league.creatorUserName || 'Player'}
          </Text>
          {!!league.description && (
            <Text style={st.description}>{league.description}</Text>
          )}
          <View style={st.statRow}>
            {critsOn && (
              <View style={st.stat}>
                <Text style={st.statVal}>{fmtCrits(league.potCount || 0)}</Text>
                <Text style={st.statLabel}>Crit Pool</Text>
              </View>
            )}
            <View style={st.stat}>
              <Text style={st.statVal}>{(league.rosterUids || []).length}/{league.maxPlayers}</Text>
              <Text style={st.statLabel}>{league.isTeamLeague || Number(league.teamSize || 1) > 1 ? 'Teams' : 'Players'}</Text>
            </View>
            <View style={st.stat}>
              <Text style={st.statVal}>{league.totalRounds || '—'}</Text>
              <Text style={st.statLabel}>{isRaceLeague ? (isBRLeague ? 'Matches' : 'Races') : 'Rounds'}</Text>
            </View>
            {critsOn && (
              <View style={st.stat}>
                <Text style={st.statVal}>{fmtCrits(league.entryCostCrits || 0)}</Text>
                <Text style={st.statLabel}>Entry</Text>
              </View>
            )}
          </View>
          <Text style={st.subtle}>
            {league.status === 'registration'
              ? `${isRaceLeague ? (isBRLeague ? 'Match 1' : 'Race 1') : 'Round 1'} starts ${fmtWhen(startMs)} — registration closes 15 minutes before.`
              : `Started ${fmtWhen(startMs)}.`}
            {'  '}
            {isRaceLeague
              ? (isBRLeague
                ? `${league.racesCount || league.totalRounds} full-lobby matches, placement points${league.hostMode === 'dedicated' ? ', dedicated host' : ', rotating player-host'}.`
                : `${league.racesCount || league.totalRounds} full-grid races, points scoring${league.hostMode === 'dedicated' ? ', dedicated host' : ', rotating player-host'}.`)
              : `Each opponent ${league.meetingsPerOpponent || 1}×.`}
            {calendarRhythm ? `  ${calendarRhythm}.` : ''}
            {/* Placement payout split: Crits-ON only (results and placements are never paid). */}
            {critsOn && (<>{'  '}Payout: {league.payoutMode}{splits ? ` (${splits.join('/')})` : ''}.</>)}
          </Text>
          {critsOn && !!league.crossesSeasonEnd && league.status !== 'complete' && league.status !== 'cancelled' && (
            <Text style={[st.subtle, { color: '#f5b342' }]}>
              This league runs past the current CritCash season end. It keeps going{critsOn ? ', and its Crit pool stays with the league' : ''}.
            </Text>
          )}

          {/* Manual-results games (every non-vision game): joiners commit
              their entry knowing how results settle. */}
          {gameUsesManualResults(league.game) && (
            <Text style={st.subtle}>
              Results are reported by the players: when the {isRaceLeague ? 'race' : 'match'} ends,
              everyone taps MATCH ENDED and reports — matching reports post instantly, and anything
              contested gets reviewed.
            </Text>
          )}

          {/* Actions */}
          {canJoin && (
            <TouchableOpacity style={st.primaryBtn} disabled={busy} onPress={() => call('joinLeague')}>
              {busy ? <ActivityIndicator color="#000" />
                : <Text style={st.primaryBtnText}>
                  {(league.isTeamLeague || Number(league.teamSize || 1) > 1)
                    ? (critsOn ? `Reserve team spot — pay your share (${fmtCrits(league.entryCostCrits || 0)})` : 'Reserve team spot')
                    : (critsOn ? `Join for ${fmtCrits(league.entryCostCrits || 0)}` : 'Join league')}
                </Text>}
            </TouchableOpacity>
          )}
          {canLeave && (
            <TouchableOpacity style={st.secondaryBtn} disabled={busy}
              onPress={() => call('leaveLeague', critsOn ? 'Leave this league? Your entry is refunded in full.' : 'Leave this league?')}>
              <Text style={st.secondaryBtnText}>{critsOn ? 'Leave (full refund)' : 'Leave league'}</Text>
            </TouchableOpacity>
          )}
          {canCancel && (
            <TouchableOpacity style={st.dangerBtn} disabled={busy}
              onPress={() => call('cancelLeague', critsOn ? 'Cancel this league? Everyone (including you) is refunded in full.' : 'Cancel this league? Everyone is removed from it.')}>
              <Text style={st.dangerBtnText}>{critsOn ? 'Cancel league (refund everyone)' : 'Cancel league'}</Text>
            </TouchableOpacity>
          )}
          {isCreator && league.status === 'complete' && league.fillCertification?.status === 'certified' && (
            <TouchableOpacity style={st.primaryBtn} disabled={runningItBack} onPress={runItBack}>
              {runningItBack ? <ActivityIndicator color="#000" />
                : <Text style={st.primaryBtnText}>Run It Back</Text>}
            </TouchableOpacity>
          )}
          {isCreator && league.status === 'complete' && league.fillCertification?.status === 'pending_settle' && (
            <Text style={st.memberNote}>Sellout under review — Run It Back unlocks when it certifies.</Text>
          )}
          {isMember && league.status === 'registration' && (
            <Text style={st.memberNote}>{critsOn ? 'You\'re in. No refunds once the league locks — a dropout forfeits their remaining matches.' : 'You\'re in. Once the league locks, a dropout forfeits their remaining matches.'}</Text>
          )}
          {/* Command Center — everyone can view the configuration; only the
              commissioner can edit (server-enforced). */}
          <TouchableOpacity style={st.commandBtn}
            onPress={() => navigation.navigate('LeagueCommandCenter', { leagueId })}>
            <Text style={st.commandBtnText}>
              {isCreator
                ? `⚙️ Command Center${league.status === 'registration' ? ' — tune the competition' : ''}`
                : '⚙️ View league settings'}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Team funding (team leagues, squad members) */}
        {mySquadEntry && league.status === 'registration' && (() => {
          const { info } = mySquadEntry;
          const paid = info.paidUids || [];
          const required = info.requiredShares || league.teamSize || 0;
          const iPaid = paid.includes(uid);
          const owed = Math.max(0, required - paid.length);
          return (
            <View style={st.lineupCard}>
              <Text style={st.lineupTitle}>
                {info.userName}: {paid.length}/{required} shares {critsOn ? 'paid' : 'confirmed'}
                {critsOn
                  ? (owed > 0 ? ` — ${fmtCrits(owed * (league.entryCostCrits || 0))} still owed` : ' — fully funded ✓')
                  : (owed > 0 ? ` — ${owed} still to confirm` : ' — squad confirmed ✓')}
              </Text>
              {owed > 0 && (
                <Text style={st.subtle}>
                  {critsOn
                    ? `The spot is reserved, not locked — if ${required} shares aren't paid before the league locks, the reservation is released and everyone who paid is refunded.`
                    : `The spot is reserved, not locked — if ${required} shares aren't confirmed before the league locks, the reservation is released.`}
                </Text>
              )}
              {!iPaid && (
                <TouchableOpacity style={[st.primaryBtn, payingShare && { opacity: 0.4 }]}
                  disabled={payingShare} onPress={payShare}>
                  {payingShare ? <ActivityIndicator color="#000" />
                    : <Text style={st.primaryBtnText}>{critsOn ? `Pay your share — ${fmtCrits(league.entryCostCrits || 0)}` : 'Confirm your share'}</Text>}
                </TouchableOpacity>
              )}
              {iPaid && <Text style={st.subtle}>{critsOn ? 'Your share is paid ✓' : 'Your share is confirmed ✓'}</Text>}
            </View>
          );
        })()}

        {/* Your lineup (team leagues, captains) */}
        {isCaptainHere && ['registration', 'active'].includes(league.status) && (
          <View style={st.lineupCard}>
            <Text style={st.lineupTitle}>
              Your lineup — pick {league.teamSize} starter{league.teamSize === 1 ? '' : 's'} ({lineup.length}/{league.teamSize})
            </Text>
            <View style={st.lineupRow}>
              {(myTeamInfo.squadUids || savedLineup).map((m) => {
                const eligible = !!myTeamInfo.memberIgns && (m in myTeamInfo.memberIgns);
                const unpaid = !!myTeamInfo.paidUids && !myTeamInfo.paidUids.includes(m);
                const selectable = eligible && !unpaid;
                const on = lineup.includes(m);
                return (
                  <TouchableOpacity key={m}
                    style={[st.lineupPill, on && st.lineupPillOn, !selectable && st.lineupPillDisabled]}
                    disabled={!selectable}
                    onPress={() => toggleStarter(m)}>
                    <Text style={[st.lineupPillText, on && st.lineupPillTextOn]} numberOfLines={1}>
                      {myTeamInfo.memberIgns?.[m] || m.slice(0, 8)}{m === uid ? ' (you)' : ''}
                      {!eligible ? ' · not ready' : unpaid ? (critsOn ? ' · unpaid' : ' · unconfirmed') : ''}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            <Text style={st.subtle}>
              Alternates sit out. Changes apply to your matches that haven't locked yet
              (rounds lock 15 minutes before their start).
            </Text>
            {lineupDirty && (
              <TouchableOpacity
                style={[st.primaryBtn, (lineup.length !== league.teamSize || savingLineup) && { opacity: 0.4 }]}
                disabled={lineup.length !== league.teamSize || savingLineup}
                onPress={saveLineup}>
                {savingLineup ? <ActivityIndicator color="#000" /> : <Text style={st.primaryBtnText}>Save lineup</Text>}
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* Standings */}
        <Text style={st.sectionHeader}>
          {league.status === 'complete' ? 'Final Standings' : 'Standings'}
        </Text>
        <View style={st.table}>
          <View style={st.headRow}>
            <Text style={[st.hCell, { width: 30 }]}>#</Text>
            <Text style={[st.hCell, { flex: 1 }]}>{(league.isTeamLeague || Number(league.teamSize || 1) > 1) ? 'TEAM' : 'PLAYER'}</Text>
            {isRaceLeague ? (
              <>
                <Text style={[st.hNum, { width: 44 }]}>PTS</Text>
                <Text style={st.hNum}>WINS</Text>
              </>
            ) : (
              <>
                <Text style={st.hNum}>W</Text>
                <Text style={st.hNum}>L</Text>
              </>
            )}
            <Text style={st.hNum}>FF</Text>
            {critsOn && league.status === 'complete' && <Text style={[st.hNum, { width: 70 }]}>PAYOUT</Text>}
          </View>
          {standingsRows.map((row) => (
            <TouchableOpacity key={row.userId} style={st.row}
              onPress={() => navigation.navigate('PublicProfile', {
                // Team leagues show the TEAM name — profiles link to the captain.
                userName: league.roster?.[row.userId]?.captainUserName || row.userName,
              })}>
              <Text style={[st.cell, { width: 30, color: row.rank === 1 ? GOLD : row.rank === 2 ? '#C0C0C0' : row.rank === 3 ? '#CD7F32' : '#888' }]}>
                {row.rank}
              </Text>
              <Text style={[st.cell, { flex: 1, fontWeight: '800' }]} numberOfLines={1}>
                {row.userName}{row.userId === league.createdByUid ? ' 👑' : ''}
              </Text>
              {isRaceLeague ? (
                <>
                  <Text style={[st.num, { width: 44, color: GOLD }]}>{row.points || 0}</Text>
                  <Text style={[st.num, { color: '#4ade80' }]}>{row.wins || 0}</Text>
                </>
              ) : (
                <>
                  <Text style={[st.num, { color: '#4ade80' }]}>{row.w}</Text>
                  <Text style={[st.num, { color: '#ff6b6b' }]}>{row.l}</Text>
                </>
              )}
              <Text style={st.num}>{row.forfeits}</Text>
              {critsOn && league.status === 'complete' && (
                <Text style={[st.num, { width: 70, color: row.payoutCrits > 0 ? GOLD : '#666' }]}>
                  {row.payoutCrits > 0 ? fmtCrits(row.payoutCrits) : '—'}
                </Text>
              )}
            </TouchableOpacity>
          ))}
          {standingsRows.length === 0 && (
            <Text style={[st.subtle, { padding: 10 }]}>No players yet — be the first to join.</Text>
          )}
        </View>
        {isRaceLeague && (
          <Text style={[st.subtle, { marginTop: -8, marginBottom: 12 }]}>
            {isBRLeague
              ? 'Placement points: 30 for P1, sliding down to 2 by P20 (0 beyond). DNF scores 0; a no-show also counts a forfeit.'
              : 'Points scoring: 25-18-15-12-10-8-6-4-2-1 for P1–P10. DNF scores 0; a no-show also counts a forfeit.'}
          </Text>
        )}

        {/* Schedule — the whole calendar (every planned round), with each
            round's matches nested once they exist. */}
        {plannedRounds.length > 0 && (
          <>
            <Text style={st.sectionHeader}>Schedule</Text>
            {league.status === 'registration' && !isRaceLeague && (
              <Text style={[st.subtle, { marginBottom: 6 }]}>
                Dates assume a full roster — fewer joins at lock means fewer rounds, never more.
              </Text>
            )}
            {plannedRounds.map(({ round, startMs: roundStartMs, matches: roundMatches }) => (
              <View key={round} style={st.roundBlock}>
                <Text style={st.roundTitle}>
                  {isRaceLeague ? (isBRLeague ? 'Match' : 'Race') : 'Round'} {round}
                  {round === league.totalRounds ? ' (Final)' : ''}
                  {'  '}
                  <Text style={st.roundWhen}>{fmtWhen(roundStartMs)}</Text>
                  {roundMatches.length === 0 && (
                    <Text style={st.roundPending}>
                      {'  '}{league.status === 'registration' ? 'Scheduled' : (round <= (league.currentRound || 0) ? 'Pending' : 'Upcoming')}
                    </Text>
                  )}
                </Text>
                {isRaceLeague
                  ? roundMatches.map((m) => {
                    const done = ['completed', 'finished'].includes(m.status);
                    const voided = ['cancelled', 'canceled'].includes(m.status);
                    const hostName = league.roster?.[m.hostUserId]?.userName
                      || (m.leagueHostMode === 'dedicated' ? 'Dedicated host' : 'TBD');
                    const iAmHost = !!uid && m.hostUserId === uid;
                    const winnerName = m.championId
                      ? (league.roster?.[m.championId]?.userName || m.championName || 'Winner')
                      : null;
                    const raceResult = league.matchResults?.[m.id];
                    return (
                      <View key={m.id}>
                        <TouchableOpacity style={st.matchRow}
                          onPress={() => navigation.navigate('EventDetailScreen', { id: m.id, eventId: m.id, collection: 'tournaments' })}>
                          <Text style={st.matchNames} numberOfLines={1}>
                            {m.track ? `${m.track}` : (isBRLeague ? 'Battle royale match' : 'Full-grid race')}
                            <Text style={st.vsText}>  ·  Host: {hostName}</Text>
                            {done && winnerName ? <Text style={st.winName}>  🏁 {winnerName}</Text> : null}
                          </Text>
                          <Text style={[st.matchStatus,
                            m.status === 'live' && { color: '#2ECC71' },
                            done && { color: GOLD },
                            voided && { color: '#ff6b6b' }]}>
                            {voided ? 'Voided' : MATCH_STATUS_LABEL(m.status)}
                          </Text>
                        </TouchableOpacity>
                        {/* Room ID / game code: host submits it; everyone else reads it */}
                        {!voided && !done && m.roomId && (
                          isBRLeague ? (
                            <Text style={st.roomIdLine}>
                              Game Code: <Text style={st.roomIdCode}>{m.roomId}</Text> - join the private match with this code
                            </Text>
                          ) : (
                            <Text style={st.roomIdLine}>
                              Room ID: <Text style={st.roomIdCode}>{m.roomId}</Text> — GT7 → Lobby → Enter Room ID
                            </Text>
                          )
                        )}
                        {iAmHost && !voided && !done && m.awaitingRoomId && ['locked', 'live'].includes(m.status) && (
                          <View style={st.roomSubmitRow}>
                            <TextInput
                              style={st.roomInput}
                              value={roomIdInput}
                              onChangeText={setRoomIdInput}
                              placeholder={isBRLeague ? '6-char game code' : '6-char Room ID'}
                              placeholderTextColor="#666"
                              maxLength={6}
                              autoCapitalize="none"
                            />
                            <TouchableOpacity style={st.roomSubmitBtn} disabled={submittingRoom}
                              onPress={() => submitRoomId(m.id)}>
                              {submittingRoom
                                ? <ActivityIndicator color="#000" size="small" />
                                : <Text style={st.roomSubmitText}>{isBRLeague ? 'Share Code' : 'Share Room'}</Text>}
                            </TouchableOpacity>
                          </View>
                        )}
                        {iAmHost && !voided && !done && m.status === 'scheduled' && (
                          isBRLeague ? (
                            <Text style={st.roomIdLine}>You host this match - the game code box appears here when it locks.</Text>
                          ) : (
                            <Text style={st.roomIdLine}>You host this race — the Room ID box appears here when it locks.</Text>
                          )
                        )}
                        {/* Finishing order once scored */}
                        {done && raceResult?.results && (
                          <Text style={st.roomIdLine} numberOfLines={2}>
                            {Object.entries(raceResult.results)
                              .filter(([, r]) => r)
                              .sort((x, y) => x[1] - y[1])
                              .slice(0, 5)
                              .map(([u, r]) => `P${r} ${league.roster?.[u]?.userName || 'Player'}`)
                              .join('  ·  ')}
                          </Text>
                        )}
                      </View>
                    );
                  })
                  : roundMatches.map((m) => {
                    const [a, b] = m.leagueUids || [];
                    const nameA = league.roster?.[a]?.userName || 'Player';
                    const nameB = league.roster?.[b]?.userName || 'Player';
                    const done = ['completed', 'finished'].includes(m.status);
                    const winner = m.championId;
                    return (
                      <TouchableOpacity key={m.id} style={st.matchRow}
                        onPress={() => navigation.navigate('EventDetailScreen', { id: m.id, eventId: m.id, collection: 'tournaments' })}>
                        <Text style={st.matchNames} numberOfLines={1}>
                          <Text style={done && winner === a ? st.winName : undefined}>{nameA}</Text>
                          <Text style={st.vsText}>  vs  </Text>
                          <Text style={done && winner === b ? st.winName : undefined}>{nameB}</Text>
                        </Text>
                        <Text style={[st.matchStatus,
                          m.status === 'live' && { color: '#2ECC71' },
                          done && { color: GOLD }]}>
                          {MATCH_STATUS_LABEL(m.status)}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
              </View>
            ))}
          </>
        )}

        {/* Registration-phase footnote */}
        {league.status === 'registration' && (
          <Text style={[st.subtle, { marginTop: 8 }]}>
            {isRaceLeague
              ? `The ${isBRLeague ? 'match' : 'race'} calendar is drawn when the league locks. Lobby-host duty rotates through the roster in join order.`
              : 'Matchups are drawn when the league locks. Odd roster → one bye per round.'}
          </Text>
        )}
      </ScrollView>

      <BuyCritsModal
        visible={!!critShortfall}
        shortfall={critShortfall}
        onClose={() => setCritShortfall(null)}
        onPurchased={() => setCritShortfall(null)}
        onUpgradePlan={() => navigation.navigate('Subscription')}
      />

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

const st = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#0b0b0b' },
  container: { padding: 12, paddingBottom: 120, maxWidth: 860, width: '100%', alignSelf: 'center' },
  hero: {
    backgroundColor: '#0f0f0f', borderRadius: 12, padding: 14,
    borderWidth: 1, borderColor: '#1a1a1a', marginBottom: 16,
  },
  heroTop: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 },
  title: { color: '#fff', fontSize: 20, fontWeight: '900', flex: 1 },
  statusChip: {
    color: '#000', backgroundColor: '#9aa0b4', fontSize: 10, fontWeight: '900',
    paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6, overflow: 'hidden',
  },
  statusActive: { backgroundColor: '#2ECC71' },
  statusDone: { backgroundColor: GOLD },
  statusCancelled: { backgroundColor: '#e74c3c', color: '#fff' },
  subtle: { color: '#9aa0b4', fontSize: 12, marginTop: 6, lineHeight: 17 },
  description: {
    color: '#cfcfcf', fontSize: 13, lineHeight: 19, marginTop: 10,
    backgroundColor: '#111', borderRadius: 8, padding: 10,
    borderLeftWidth: 3, borderLeftColor: '#2a2f36',
  },
  commandBtn: {
    borderColor: '#4FC3F7', borderWidth: 1, borderRadius: 10, paddingVertical: 10,
    alignItems: 'center', marginTop: 8,
  },
  commandBtnText: { color: '#4FC3F7', fontWeight: '800', fontSize: 13 },
  statRow: { flexDirection: 'row', gap: 10, marginTop: 12 },
  stat: {
    flex: 1, backgroundColor: '#151515', borderRadius: 10,
    paddingVertical: 8, alignItems: 'center',
  },
  statVal: { color: GOLD, fontSize: 15, fontWeight: '900' },
  statLabel: { color: '#8a8a8a', fontSize: 10, fontWeight: '700', marginTop: 2 },
  primaryBtn: {
    backgroundColor: GOLD, borderRadius: 10, paddingVertical: 12,
    alignItems: 'center', marginTop: 12,
  },
  primaryBtnText: { color: '#000', fontWeight: '900', fontSize: 14 },
  secondaryBtn: {
    borderColor: '#444', borderWidth: 1, borderRadius: 10, paddingVertical: 10,
    alignItems: 'center', marginTop: 8,
  },
  secondaryBtnText: { color: '#ccc', fontWeight: '800', fontSize: 13 },
  dangerBtn: {
    borderColor: '#e74c3c', borderWidth: 1, borderRadius: 10, paddingVertical: 10,
    alignItems: 'center', marginTop: 8,
  },
  dangerBtnText: { color: '#e74c3c', fontWeight: '800', fontSize: 13 },
  memberNote: { color: '#8a8a8a', fontSize: 11, marginTop: 8 },

  sectionHeader: { color: '#fff', fontSize: 16, fontWeight: '900', marginBottom: 8, marginTop: 4 },
  table: {
    backgroundColor: '#0f0f0f', borderRadius: 12, borderWidth: 1,
    borderColor: '#1a1a1a', overflow: 'hidden', marginBottom: 16,
  },
  headRow: {
    flexDirection: 'row', paddingVertical: 8, paddingHorizontal: 10,
    borderBottomWidth: 1, borderBottomColor: '#1e1e1e', backgroundColor: '#121212',
  },
  hCell: { color: '#8a8a8a', fontSize: 10, fontWeight: '900' },
  hNum: { color: '#8a8a8a', fontSize: 10, fontWeight: '900', width: 34, textAlign: 'right' },
  row: {
    flexDirection: 'row', paddingVertical: 9, paddingHorizontal: 10,
    borderBottomWidth: 1, borderBottomColor: '#161616', alignItems: 'center',
  },
  cell: { color: '#e7ecff', fontSize: 13 },
  num: { color: '#cfcfcf', fontSize: 13, width: 34, textAlign: 'right', fontWeight: '700' },

  roundBlock: {
    backgroundColor: '#0f0f0f', borderRadius: 12, borderWidth: 1,
    borderColor: '#1a1a1a', padding: 10, marginBottom: 10,
  },
  roundTitle: { color: '#fff', fontSize: 13, fontWeight: '900', marginBottom: 6 },
  roundWhen: { color: '#8a8a8a', fontSize: 11, fontWeight: '600' },
  roundPending: { color: '#8a8a8a', fontSize: 10, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.5 },
  matchRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingVertical: 8, borderTopWidth: 1, borderTopColor: '#161616',
  },
  matchNames: { color: '#cfcfcf', fontSize: 13, flex: 1, marginRight: 8 },
  winName: { color: GOLD, fontWeight: '900' },
  vsText: { color: '#666', fontSize: 11 },
  matchStatus: { color: '#9aa0b4', fontSize: 11, fontWeight: '900' },

  // Lineup card (team leagues, captains)
  lineupCard: {
    backgroundColor: '#0f0f0f', borderRadius: 12, borderWidth: 1,
    borderColor: '#4FC3F766', padding: 12, marginBottom: 16,
  },
  lineupTitle: { color: '#4FC3F7', fontSize: 13, fontWeight: '900', marginBottom: 8 },
  lineupRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  lineupPill: {
    borderWidth: 1, borderColor: '#2a2a2a', backgroundColor: '#151515',
    paddingVertical: 6, paddingHorizontal: 10, borderRadius: 10, maxWidth: 200,
  },
  lineupPillOn: { backgroundColor: '#4FC3F7', borderColor: '#4FC3F7' },
  lineupPillDisabled: { opacity: 0.4 },
  lineupPillText: { color: '#ddd', fontWeight: '800', fontSize: 12 },
  lineupPillTextOn: { color: '#000' },

  // Race rounds: room-id + results strip
  roomIdLine: { color: '#8a8a8a', fontSize: 11, paddingBottom: 8, paddingLeft: 2 },
  roomIdCode: { color: '#4FC3F7', fontWeight: '900', fontSize: 13, letterSpacing: 1 },
  roomSubmitRow: { flexDirection: 'row', gap: 8, paddingBottom: 10, alignItems: 'center' },
  roomInput: {
    flex: 1, backgroundColor: '#0f1115', borderColor: '#2a2f36', borderWidth: 1,
    borderRadius: 8, paddingHorizontal: 10, paddingVertical: 8, color: '#fff', fontSize: 13,
  },
  roomSubmitBtn: {
    backgroundColor: GOLD, borderRadius: 8, paddingVertical: 9, paddingHorizontal: 14,
  },
  roomSubmitText: { color: '#000', fontWeight: '900', fontSize: 12 },
});
